import React, { useEffect, useState } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createBrowserRouter, Navigate, useRouteError, useLocation } from 'react-router-dom';
import '../src/assets/css/custom.css';
import '../src/index.css';
import Auth from "./layout/Auth";
import LayoutMain from "./layout/LayoutMain";
import LayoutProjectView from "./layout/LayoutProjectView";
import routes from "./routes";
import { WebSocketProvider } from "./services/websocket/WebSocketContext";
import Landing, { LandingHubLayout } from './layout/Landing';
import StorageMain from './pages/storage/StorageMain';
import StorageView from './pages/storage/StorageView';
// import LocalStoragePage from './pages/localStorage/LocalStoragePage';
import AgentWorkflow from './pages/apps/AgentWorkflow';
import ProjectTracesPage from './pages/apps/ProjectTracesPage';
import ImportApplicationPage from './pages/apps/ImportApplicationPage';
import BillingPage from './pages/billing/BillingPage';
import SettingsPage from './pages/settings/SettingsPage';
import Stack from "./pages/stack/stack"
import CloudList from './pages/cloud/CloudList';
import ClusterList from './pages/cloud/ClusterList';
import ClusterConfig from './pages/cloud/ClusterConfig';
import ClusterWorkloads from './pages/cloud/ClusterWorkloads';
import AccountsList from './pages/accounts/AccountsList';
import Evaluation from './pages/Evaluation';
import DashboardPage from './pages/dashboard/DashboardPage';
import AuditHistoryPage from './pages/governance/AuditHistoryPage';
import NotFound from './pages/NotFound';
import ErrorPage from './pages/ErrorPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import RuntimeErrorHandler from './components/common/RuntimeErrorHandler';
import CookieConsent from './components/common/CookieConsent';
import { NotificationProvider } from './components/common/NotificationProvider';
import { TENANT_SWITCH_OVERLAY_ID, TENANT_SWITCH_SESSION_KEY } from "./settings/shared/constants";
import { requestNotificationPermission } from "./assets/js/notificationUtils";
import MarketplaceGate from './pages/auth/MarketplaceGate';

function dismissTenantSwitchOverlay() {
  try {
    sessionStorage.removeItem(TENANT_SWITCH_SESSION_KEY);
    const el = document.getElementById(TENANT_SWITCH_OVERLAY_ID);
    if (!el) return;
    el.style.transition = "opacity 0.28s ease";
    el.style.opacity = "0";
    window.setTimeout(() => {
      try {
        el.classList.remove("sa-tenant-switch-overlay--visible");
        el.remove();
      } catch (e2) {
        /* ignore */
      }
    }, 300);
  } catch (e) {
    /* ignore */
  }
}

const RouterRoot = () => {
  React.useEffect(() => {
    const enableNotification = async () => {
      dismissTenantSwitchOverlay();
      const enabled = await requestNotificationPermission();
    };
    enableNotification();
  }, []);

  return <RouterProvider router={router} />;
};
  
// Filter routes by layout
const mainRoutes = routes.filter(route => route.layout === "/");
const authRoutes = routes.filter(route => route.layout === "/auth");

/** Rendered inside `LandingHubLayout` (shared left nav) — omit from flat `mainRoutes` map */
const HUB_NESTED_ROUTE_PATHS = new Set(["storage", "storageView", "local-storage", "settings", "stack", "dashboard", "cloud", "account","clusters"]);

// Error element component for React Router
// Note: ErrorPage only shows for 500 errors, so router errors won't display it
const RouterErrorElement = () => {
  const error = useRouteError();
  
  // Check if error has statusCode 500 (from backend)
  const statusCode = error?.response?.status || error?.status || error?.statusCode;
  
  return (
    <ErrorPage
      error={error}
      errorInfo={error?.stack ? { componentStack: error.stack } : null}
      errorMessage={error?.message || 'An error occurred'}
      isRuntimeError={true}
      statusCode={statusCode}
      show={true}
      onHide={() => window.location.reload()}
      onReset={() => window.location.reload()}
    />
  );
};

/** Desktop opens /login?... — mirror query into /auth/login */
const LoginAliasRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/auth/login${location.search}${location.hash}`} replace />;
};

const LogoutAliasRedirect = () => {
  try {
    sessionStorage.removeItem("desktopOAuth");
  } catch (e) {
    /* ignore */
  }
  return <Navigate to="/auth/login" replace />;
};


const router = createBrowserRouter([
  {
    element: <MarketplaceGate />,
    children: [
  {
    path: "/",
    element: <LayoutMain />,
    // errorElement: <RouterErrorElement />,
    children: [
      {
        index: true,
        element: <Navigate to="apps/" replace />
      },
      {
        element: <LandingHubLayout />,
        children: [
          { path: "apps", element: <Navigate to="/apps/" replace /> },
          { path: "apps/", element: <Landing /> },
          { path: "apps/import", element: <ImportApplicationPage /> },
          { path: "apps/import/:applicationId", element: <ImportApplicationPage /> },
          { path: "xray", element: <ImportApplicationPage /> },
          { path: "xray/:applicationId", element: <ImportApplicationPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "Stack", element: <Stack /> },
          { path: "cloud", element: <CloudList /> },
          { path: "clusters", element: <ClusterList /> },
          { path: "clusters/configuration", element: <ClusterConfig /> },
          { path: "clusters/resources", element: <ClusterWorkloads /> },
          { path: "accounts", element: <AccountsList /> },
          { path: "dashboard", element: <DashboardPage /> },
          { path: "audit-history", element: <AuditHistoryPage /> },
          { path: "storage", element: <StorageMain /> },
          { path: "storageView", element: <StorageView /> },
          // { path: "storage", element: <LocalStoragePage /> },
          { path: "evaluation/:projectId", element: <Evaluation /> },
        ],
      },
      {
        path: "app/agent",
        element: <AgentWorkflow />
      },
      {
        path: "project/:id/traces",
        element: <ProjectTracesPage />
      },
      {
        path: "project/:id",
        element: <LayoutProjectView />
      },
      {
        path: "billing",
        element: <BillingPage />
      },
      ...mainRoutes
        .filter((route) => !HUB_NESTED_ROUTE_PATHS.has(route.path))
        .map((route) => ({
          path: route.path,
          element: route.element,
          errorElement: <RouterErrorElement />
        }))
    ]
  },
  {
    path: "/auth",
    element: <Auth />,
    errorElement: <RouterErrorElement />,
    children: authRoutes.map(route => ({
      path: route.path,
      element: route.element,
      errorElement: <RouterErrorElement />
    }))
  },
  {
    path: "/login",
    element: <LoginAliasRedirect />,
  },
  {
    path: "/logout",
    element: <LogoutAliasRedirect />,
  },
    {
    path: "*",
    element: <NotFound />
  }
    ]
  }
])

ReactDOM.createRoot(document.getElementById("root")).render(
  <NotificationProvider>
  <ErrorBoundary>
    <WebSocketProvider>
      <RuntimeErrorHandler />
      <RouterRoot />
      <CookieConsent />
    </WebSocketProvider>
  </ErrorBoundary>
  </NotificationProvider>
);
